import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const ReviewsRatings = () => {
  const { currentUser } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [averageRating, setAverageRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);

  // Mock data for demonstration
  const mockReviews = [
    {
      id: 'review1',
      reviewerName: 'Sarah M.',
      rating: 5,
      comment: 'Excellent session! Steve really helped me improve my interview skills. His feedback was constructive and actionable. I felt much more confident after our practice session.',
      createdAt: new Date('2024-10-28'),
      sessionId: 'session1'
    },
    {
      id: 'review2',
      reviewerName: 'Michael K.',
      rating: 4,
      comment: 'Great experience overall. Steve was patient and provided helpful tips for public speaking. Would definitely recommend for anyone looking to improve their communication skills.',
      createdAt: new Date('2024-10-25'),
      sessionId: 'session2'
    },
    {
      id: 'review3',
      reviewerName: 'Jennifer L.',
      rating: 5,
      comment: 'Outstanding coaching session! Steve helped me prepare for a technical presentation and I nailed it. His approach is very professional and encouraging.',
      createdAt: new Date('2024-10-20'),
      sessionId: 'session3'
    },
    {
      id: 'review4',
      reviewerName: 'David R.',
      rating: 4,
      comment: 'Good session focused on networking skills. Steve provided practical advice that I could immediately apply. The role-playing exercises were particularly helpful.',
      createdAt: new Date('2024-10-15'),
      sessionId: 'session4'
    },
    {
      id: 'review5',
      reviewerName: 'Lisa W.',
      rating: 5,
      comment: 'Amazing experience! Steve helped me overcome my fear of public speaking. His techniques are really effective and he creates a very comfortable learning environment.',
      createdAt: new Date('2024-10-10'),
      sessionId: 'session5'
    },
    {
      id: 'review6',
      reviewerName: 'James B.',
      rating: 3,
      comment: 'Decent session. Steve knows his stuff but I was hoping for more personalized feedback. Still learned some useful techniques for business presentations.',
      createdAt: new Date('2024-10-05'),
      sessionId: 'session6'
    },
    {
      id: 'review7',
      reviewerName: 'Amanda T.',
      rating: 5,
      comment: 'Fantastic coaching! Steve helped me prepare for a job interview and I got the position! His mock interview sessions were invaluable. Highly recommend!',
      createdAt: new Date('2024-09-30'),
      sessionId: 'session7'
    },
    {
      id: 'review8',
      reviewerName: 'Robert H.',
      rating: 4,
      comment: 'Very helpful session on presentation skills. Steve provided clear guidance on structuring presentations and managing nervousness. Professional and knowledgeable.',
      createdAt: new Date('2024-09-25'),
      sessionId: 'session8'
    }
  ];

  useEffect(() => {
    // Simulate loading and calculating stats
    setTimeout(() => {
      setReviews(mockReviews);
      setTotalReviews(mockReviews.length);
      
      const totalRating = mockReviews.reduce((sum, review) => sum + review.rating, 0);
      const avgRating = totalRating / mockReviews.length;
      setAverageRating(Math.round(avgRating * 10) / 10);
      
      setLoading(false);
    }, 1000);
  }, []);

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (i <= rating) {
        stars.push(<span key={i} className="star full">★</span>);
      } else if (i - 0.5 <= rating) {
        stars.push(<span key={i} className="star half">★</span>);
      } else {
        stars.push(<span key={i} className="star empty">☆</span>);
      }
    }
    return stars;
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    }).format(date);
  };

  const getRatingDistribution = () => {
    const distribution = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
    reviews.forEach(review => {
      distribution[review.rating]++;
    });
    return distribution;
  };

  const ratingDistribution = getRatingDistribution();

  if (loading) {
    return <div className="loading">Loading reviews...</div>;
  }

  return (
    <div className="reviews-ratings-page">
      <div className="reviews-container">
        <div className="reviews-header">
          <h2>My Reviews and Ratings</h2>
          <p>See what your clients are saying about your coaching sessions</p>
        </div>

        <div className="reviews-summary">
          <div className="summary-card overall-rating">
            <div className="rating-number">{averageRating}</div>
            <div className="rating-stars">
              {renderStars(averageRating)}
            </div>
            <div className="rating-text">
              Based on {totalReviews} review{totalReviews !== 1 ? 's' : ''}
            </div>
          </div>

          <div className="summary-card rating-breakdown">
            <h3>Rating Breakdown</h3>
            {[5, 4, 3, 2, 1].map(rating => (
              <div key={rating} className="rating-bar">
                <span className="rating-label">{rating} star{rating !== 1 ? 's' : ''}</span>
                <div className="bar-container">
                  <div 
                    className="bar-fill" 
                    style={{ 
                      width: `${totalReviews > 0 ? (ratingDistribution[rating] / totalReviews) * 100 : 0}%` 
                    }}
                  ></div>
                </div>
                <span className="rating-count">({ratingDistribution[rating]})</span>
              </div>
            ))}
          </div>
        </div>

        <div className="reviews-list">
          <h3>Individual Reviews</h3>
          {reviews.length === 0 ? (
            <div className="no-reviews">
              <p>No reviews yet. Keep providing great coaching sessions and reviews will start coming in!</p>
            </div>
          ) : (
            <div className="reviews-grid">
              {reviews.map(review => (
                <div key={review.id} className="review-card">
                  <div className="review-header">
                    <div className="reviewer-info">
                      <div className="reviewer-name">{review.reviewerName}</div>
                      <div className="review-date">{formatDate(review.createdAt)}</div>
                    </div>
                    <div className="review-rating">
                      {renderStars(review.rating)}
                    </div>
                  </div>
                  <div className="review-comment">
                    {review.comment}
                  </div>
                  {review.sessionId && (
                    <div className="session-reference">
                      Session ID: {review.sessionId}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ReviewsRatings;